import {
  AspectRatio,
  Flex,
  Heading,
  Icon,
  IconButton,
  LinkBox,
  LinkOverlay,
  Stack,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { RecipeHomepageResponse } from '@diamond/shared/types';
import { RecipeTag, showToast } from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useRouteQuery, useRouteRedirection } from '@diamond/sol-buyer/utils';
import {
  RECIPE_QUERY_KEY,
  useIsAuthenticated,
  usePostRecipeFavoriteMutation,
} from '@diamond/sol-buyer-context';
import { FavoriteBorder, FavoriteOutlined } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useCallback } from 'react';

import { Image } from '../common';
import { ViewCount } from './ViewCount';

type RecipeCardMobileProps = {
  recipe: RecipeHomepageResponse['recipes'][number];
  onFavoriteClick?: (recipeId: string) => void;
  handleTrackEvent?: (
    recipe: RecipeHomepageResponse['recipes'][number]
  ) => void;
  section?: string;
};

export const RecipeCardMobile = ({
  recipe,
  onFavoriteClick,
  handleTrackEvent,
  section,
}: RecipeCardMobileProps) => {
  const tag = recipe.tags[0];

  const routeQuery = useRouteQuery();
  const isAuthenticated = useIsAuthenticated();
  const { withAuth } = useRouteRedirection({ isAuthenticated });

  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutate: postRecipeFavorite } = usePostRecipeFavoriteMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RECIPE_QUERY_KEY],
      });
    },
    onError: (errorMessage) => {
      showToast(toast, 'error', errorMessage);
    },
  });

  const handleFavorite = useCallback(
    async (recipeId: string) => {
      await withAuth(
        () => {
          postRecipeFavorite(recipeId);
        },
        'recipes',
        routeQuery?.['recipesId']
      );

      if (!recipe.is_favorite) {
        trackEvent(TRACKING_EVENTS.ADD_TO_FAV_RECIPE, {
          recipe_name: recipe.title,
          section_name: section || TRACKING_PAGES.RECIPE,
        });
      }
    },
    [
      postRecipeFavorite,
      recipe.is_favorite,
      recipe.title,
      routeQuery,
      section,
      withAuth,
    ]
  );

  return (
    <LinkBox
      as={Stack}
      w="full"
      spacing="2"
      isolation="isolate"
      position="relative"
      data-recipe-id={recipe.id}
    >
      <IconButton
        zIndex="99"
        right="-1"
        top="3"
        variant="link"
        position="absolute"
        aria-label="Add to favorite"
        onClick={() => {
          if (onFavoriteClick) {
            onFavoriteClick(recipe.id);
          } else {
            handleFavorite(recipe.id);
          }
        }}
        icon={
          recipe.is_favorite ? (
            <Icon as={FavoriteOutlined} color="red" />
          ) : (
            <Icon as={FavoriteBorder} />
          )
        }
      />
      <AspectRatio
        flex="none"
        display="flex"
        position="relative"
        isolation="isolate"
        borderRadius="lg"
        w="full"
        ratio={1}
      >
        <Image
          alt="Recipe Illustration"
          src={recipe.thumbnail}
          fill
          style={{ borderRadius: 'inherit', objectFit: 'cover' }}
        />
      </AspectRatio>
      <VStack align="start" spacing="1">
        <LinkOverlay
          as={Link}
          href={`/recipes/${recipe.slug}`}
          onClick={() => {
            if (handleTrackEvent) {
              handleTrackEvent(recipe);
            }
          }}
        >
          <Heading fontWeight="medium" fontSize="xs" noOfLines={1}>
            {recipe.title}
          </Heading>
        </LinkOverlay>
        <Flex w="full" gap="1" justifyContent="space-between">
          <ViewCount count={recipe.views} />
          {tag && (
            <Flex flexWrap="wrap" justifyContent="flex-end" gap="2">
              <RecipeTag tag={tag} />
            </Flex>
          )}
        </Flex>
      </VStack>
    </LinkBox>
  );
};
